import React from 'react';
import PropTypes from 'prop-types';
import { useCountUp } from '../../../hooks/useCountUp';

/**
 * A figure that counts into place rather than being there already.
 *
 * Only a plain number or a simple "₹1,234.56" is animated. A StatCard's value
 * can also be an element — a truncated branch name, for instance — and those
 * are rendered exactly as they arrive, because half-animating a node would be
 * worse than not animating it.
 */
const parseFigure = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return { n: value, prefix: '', decimals: 0 };
  }
  if (typeof value === 'string') {
    const m = value.match(/^(₹)?\s*([\d,]+(?:\.\d+)?)$/);
    if (m) {
      const raw = m[2].replace(/,/g, '');
      return { n: Number(raw), prefix: m[1] || '', decimals: (raw.split('.')[1] || '').length };
    }
  }
  return null;
};

const AnimatedValue = ({ value }) => {
  // A regex over a short string, so a memo here buys nothing and costs the
  // compiler its ability to optimise the component around it.
  const parsed = parseFigure(value);

  const shown = useCountUp(parsed ? parsed.n : NaN);

  if (!parsed) return value;

  return `${parsed.prefix}${shown.toLocaleString('en-IN', {
    minimumFractionDigits: parsed.decimals,
    maximumFractionDigits: parsed.decimals,
  })}`;
};

AnimatedValue.propTypes = { value: PropTypes.node };

export const StatCard = ({ icon: _Icon, label, value, suffix, trend, className = '', onClick, iconColor = '#3b82f6' }) => {
  const safeTrend = typeof trend === 'number' ? trend : 0;
  const isPositive = safeTrend >= 0;

  return (
    <article
      onClick={onClick}
      className={`relative bg-white rounded-xl shadow-[0_2px_12px_rgba(0,0,0,0.03)] border border-slate-100 flex flex-col justify-between p-4 min-h-[95px] transition-all duration-300 overflow-hidden group hover:border-slate-200 hover:shadow-[0_6px_20px_rgba(0,0,0,0.05)] hover:-translate-y-0.5 ${onClick ? 'cursor-pointer active:scale-[0.98]' : ''} ${className}`}
      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <div className="flex justify-between items-start w-full relative z-10">
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-[0.1em] mt-0.5">{label}</span>
        {typeof trend === 'number' && (
          <div className={`flex items-center gap-1 text-[10px] font-bold ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
            {isPositive ? (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>
            ) : (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"></polyline><polyline points="16 17 22 17 22 11"></polyline></svg>
            )}
            <span>{Math.abs(safeTrend)}%</span>
          </div>
        )}
      </div>
      
      <div className="mt-3 flex flex-col relative z-10">
         <div className="flex items-baseline gap-1.5">
           <div className="text-[24px] font-bold text-[#0d0d0d] tracking-tight tabular-nums leading-none"><AnimatedValue value={value} /></div>
           {suffix && <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{suffix}</div>}
         </div>
      </div>
    </article>
  );
};

StatCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  suffix: PropTypes.string,
  trend: PropTypes.number,
  className: PropTypes.string,
  onClick: PropTypes.func,
  iconColor: PropTypes.string,
};

export const WidgetPanel = ({ title, count, children, className = '', titleColor = 'text-slate-800' }) => (
  <article className={`bg-white rounded-xl p-3 shadow-[0_2px_12px_rgba(200,205,225,0.4)] flex flex-col transition-shadow duration-300 hover:shadow-[0_4px_16px_rgba(200,205,225,0.5)] relative overflow-hidden ${className}`}>
    <header className="flex items-center justify-between mb-2 shrink-0 relative z-10 border-b border-slate-50 pb-2">
      <h3 className={`text-[12px] font-bold tracking-wide uppercase ${titleColor}`}>{title}</h3>
      {count !== undefined && <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 bg-slate-50 px-2 py-0.5 rounded-md border border-black/[0.03] shadow-sm">{count} total</span>}
    </header>
    <div className="flex-1 min-h-0 flex flex-col relative z-10">{children}</div>
  </article>
);

WidgetPanel.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.number,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  titleColor: PropTypes.string,
};

export const RoundedBarChart = ({ data, color = 'var(--theme-primary)' }) => {
  const maxVal = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex-1 w-full h-full flex flex-col">
      <div className="flex-1 flex items-end justify-between gap-2 px-2 pb-4 pt-8">
        {data.map((item, idx) => {
          const heightPct = (item.value / maxVal) * 100;
          return (
            <div key={idx} className="relative flex flex-col items-center justify-end w-full h-full group">
              <div className="absolute -top-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-20 pointer-events-none">
                <div className="bg-white px-3 py-1.5 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.1)] text-[10px] font-extrabold text-slate-700 whitespace-nowrap relative">
                  {item.label}: ₹{item.value.toFixed(0)}
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45"></div>
                </div>
              </div>
              <div className="w-full max-w-[24px] bg-slate-100 rounded-full overflow-hidden flex flex-col justify-end" style={{ height: '100%' }}>
                <div className="w-full rounded-full transition-all duration-1000 ease-out" style={{ height: `${heightPct}%`, backgroundColor: color, opacity: 0.8 }}></div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-between px-2 pt-2 border-t border-slate-100">
        {data.map((item, idx) => (
          <div key={idx} className="w-full text-center text-[10px] font-bold text-slate-400">{item.label}</div>
        ))}
      </div>
    </div>
  );
};

RoundedBarChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string.isRequired, value: PropTypes.number.isRequired })).isRequired,
  color: PropTypes.string,
};

function smoothCurve(points, tension = 0.15) {
  if (points.length < 2) return '';
  let d = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[Math.min(points.length - 1, i + 2)];
    const cp1x = p1[0] + (p2[0] - p0[0]) * tension;
    const cp1y = p1[1] + (p2[1] - p0[1]) * tension;
    const cp2x = p2[0] - (p3[0] - p1[0]) * tension;
    const cp2y = p2[1] - (p3[1] - p1[1]) * tension;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2[0]} ${p2[1]}`;
  }
  return d;
}

export const DynamicRevenueChart = ({ total, sales = [] }) => {
  const [chartType, setChartType] = React.useState('SMOOTH');
  const [timeRange, setTimeRange] = React.useState('Month');
  const [hoverIdx, setHoverIdx] = React.useState(null);
  const chartRef = React.useRef(null);

  const { dataPoints, prevDataPoints, xLabels, hoverLabels } = React.useMemo(() => {
    let pts = [], prev = [], lbls = [];
    if (timeRange === 'Month') {
      pts  = [40,48,65,63,62,75,82,80,70,55,45,40,40,42,38,35,37,40,38,34,32,42,44,46,44,42,45,42,40,45,50];
      prev = [32,28,30,45,48,42,38,35,30,28,32,36,33,35,35,30,28,32,60,62,70,60,52,48,45,42,38,40,32,25,20];
      lbls = Array(31).fill('');
      const m = new Date().toLocaleString('default', { month: 'short' });
      for (let i = 0; i < 31; i++) { if (i % 4 === 0) lbls[i] = `${m} ${String(i+1).padStart(2,'0')}`; }
    } else if (timeRange === 'Week') {
      pts  = [40,65,80,45,35,42,50];
      prev = [32,48,38,32,30,60,20];
      lbls = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    } else if (timeRange === 'Year') {
      pts  = [40,63,82,55,40,35,38,32,46,42,45,50];
      prev = [32,45,38,28,36,30,32,70,48,38,25,20];
      lbls = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    } else {
      pts  = [40,45,55,65,62,70,80,82,75,60,50,42,40,38,35,38,40,35,32,42,45,42,46,50];
      prev = [32,28,35,45,48,42,35,32,30,28,35,33,30,28,32,60,68,60,50,45,42,38,30,20];
      lbls = Array(24).fill('');
      for (let i = 0; i < 24; i += 4) lbls[i] = `${i}:00`;
    }
    const hLabels = pts.map((_, i) => {
      if (timeRange === 'Month') return `${new Date().toLocaleString('default', { month: 'short' })} ${String(i+1).padStart(2,'0')}`;
      if (timeRange === 'Week') return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][i];
      if (timeRange === 'Year') return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][i];
      return `${String(i).padStart(2,'0')}:00`;
    });
    return { dataPoints: pts, prevDataPoints: prev, xLabels: lbls, hoverLabels: hLabels };
  }, [timeRange]);

  const colors = React.useMemo(() => {
    switch (chartType) {
      case 'SMOOTH': return { primary: '#2563eb', secondary: '#ea580c', bg1: 'bg-blue-600', bg2: 'bg-orange-600' };
      case 'STEPPED': return { primary: '#2563eb', secondary: '#059669', bg1: 'bg-blue-600', bg2: 'bg-emerald-600' };
      case 'DUAL BAR': return { primary: '#7c3aed', secondary: '#0d9488', bg1: 'bg-violet-600', bg2: 'bg-teal-600' };
      default: return { primary: '#2563eb', secondary: '#ea580c', bg1: 'bg-blue-600', bg2: 'bg-orange-600' };
    }
  }, [chartType]);

  const maxVal = Math.max(...dataPoints, ...prevDataPoints, 1);
  const W = 100, H = 90;
  const isBar = chartType === 'DUAL BAR';
  const n = dataPoints.length;
  const stepX = isBar ? W / Math.max(n, 1) : W / Math.max(n - 1, 1);
  const getX = (i) => isBar ? (i * stepX + stepX / 2) : (i * stepX);
  const getY = (val) => H - (val / maxVal) * H;

  const { paths1, paths2 } = React.useMemo(() => {
    const n = dataPoints.length;
    const W = 100, H = 90;
    const isBar = chartType === 'DUAL BAR';
    const stepX = isBar ? W / Math.max(n, 1) : W / Math.max(n - 1, 1);
    const getX = (i) => isBar ? (i * stepX + stepX / 2) : (i * stepX);

    const generatePaths = (arr) => {
      const pts = arr.map((v, i) => [getX(i), H - (v / maxVal) * H]);
      let strokePath;
      if (chartType === 'STEPPED') {
        strokePath = `M ${pts[0][0]} ${pts[0][1]}`;
        for (let i = 1; i < pts.length; i++) {
          const dx = pts[i][0] - pts[i-1][0];
          const offset = dx * 0.4; // 40% horizontal, 20% slope, 40% horizontal
          strokePath += ` L ${pts[i-1][0] + offset} ${pts[i-1][1]} L ${pts[i][0] - offset} ${pts[i][1]} L ${pts[i][0]} ${pts[i][1]}`;
        }
      } else {
        strokePath = smoothCurve(pts);
      }
      const fillPath = `${strokePath} L ${pts[pts.length-1][0]} ${H} L 0 ${H} Z`;
      return { strokePath, fillPath };
    };

    if (chartType === 'DUAL BAR') return { paths1: null, paths2: null };
    return { paths1: generatePaths(dataPoints), paths2: generatePaths(prevDataPoints) };
  }, [dataPoints, prevDataPoints, chartType]);

  const yLabels = [maxVal, maxVal*0.75, maxVal*0.5, maxVal*0.25, 0].map(v => Math.round(v));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-7 flex flex-col w-full relative overflow-hidden">
      
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-8 relative z-10">
        <div className="flex flex-col">
          <h3 className="text-[12px] font-bold uppercase tracking-[0.1em] text-slate-500">Revenue Analysis</h3>
          <div className="flex items-center gap-5 mt-2">
            <div className="text-[2.2rem] leading-none font-bold text-[#0f172a] tracking-tight">₹{total.toFixed(2)}</div>
            <div className="flex items-center gap-4 mt-1">
              <div className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-full ${colors.bg1}`}></div>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest" style={{fontFamily:"'Inter',system-ui,sans-serif"}}>THIS YEAR</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className={`w-2.5 h-2.5 rounded-full ${colors.bg2}`}></div>
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest" style={{fontFamily:"'Inter',system-ui,sans-serif"}}>LAST YEAR</span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-6 mt-1">
          <div className="flex items-center gap-4" style={{fontFamily:"'Inter',sans-serif"}}>
            {['STEPPED','SMOOTH','DUAL BAR'].map(type => (
              <button type="button" key={type} onClick={() => setChartType(type)} className="flex items-center gap-1.5 cursor-pointer group outline-none">
                <div 
                  className={`w-3 h-3 rounded-full border flex items-center justify-center transition-all ${chartType===type?'':'border-slate-300 group-hover:border-slate-400'}`}
                  style={{ borderColor: chartType===type ? colors.primary : undefined }}
                >
                  {chartType===type && <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colors.primary }}></div>}
                </div>
                <span className={`text-[10px] font-semibold tracking-wide transition-colors ${chartType===type?'text-slate-800':'text-slate-500 group-hover:text-slate-700'}`}>{type}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center bg-slate-50 p-1 rounded-lg border border-slate-200" style={{fontFamily:"'Inter',sans-serif"}}>
            {['Day','Week','Month','Year'].map(range => (
              <button type="button" key={range} onClick={() => setTimeRange(range)} className={`px-4 py-1.5 text-[11px] font-medium rounded-md transition-all duration-200 ${timeRange===range?'bg-white shadow-sm border border-slate-200 text-slate-900':'text-slate-500 hover:text-slate-700 hover:bg-slate-100/50 border border-transparent'}`}>{range}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="relative w-full" style={{ height: 260 }} ref={chartRef}>
        <div className="absolute left-0 top-0 bottom-6 flex flex-col justify-between pr-3 w-12">
          {yLabels.map((v, i) => (
            <span key={i} className="text-[10px] font-medium text-slate-400 text-right w-full block">
              {v > 999 ? `${(v/1000).toFixed(1)}k` : v}
            </span>
          ))}
        </div>
        <div className="absolute left-12 right-0 top-0 bottom-6">
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-full overflow-visible">
            <defs>
              <linearGradient id="primaryGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.primary} stopOpacity="0.15"/>
                <stop offset="100%" stopColor={colors.primary} stopOpacity="0.0"/>
              </linearGradient>
              <linearGradient id="barPrimaryGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.primary} stopOpacity="1"/>
                <stop offset="100%" stopColor={colors.primary} stopOpacity="0.85"/>
              </linearGradient>
              <linearGradient id="barSecondaryGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.secondary} stopOpacity="1"/>
                <stop offset="100%" stopColor={colors.secondary} stopOpacity="0.85"/>
              </linearGradient>
              <filter id="primaryShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor={colors.primary} floodOpacity="0.15"/>
              </filter>
              <filter id="secondaryShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor={colors.secondary} floodOpacity="0.15"/>
              </filter>
            </defs>
            {/* Horizontal Grid Lines */}
            {[0,0.25,0.5,0.75,1].map((f,i) => (
              <line key={`h-${i}`} x1={0} y1={H*(1-f)} x2={W} y2={H*(1-f)} stroke="#e2e8f0" strokeWidth="0.4" />
            ))}
            {/* Vertical Grid Lines */}
            {xLabels.map((lbl, i) => {
              if (!lbl && i !== 0 && i !== xLabels.length - 1) return null;
              return (
                <line key={`v-${i}`} x1={getX(i)} y1={0} x2={getX(i)} y2={H} stroke="#e2e8f0" strokeWidth="0.4" />
              )
            })}
            {chartType === 'DUAL BAR' ? (
              dataPoints.map((val, i) => {
                const bW = stepX * 0.42;
                const rx = Math.min(bW * 0.4, 1.5);
                const bH1 = (val / maxVal) * H;
                const bH2 = (prevDataPoints[i] / maxVal) * H;
                const bx = (i * stepX + stepX / 2);
                
                const drawBar = (x, w, h, gradUrl, shadowUrl) => {
                  if (h < 0.1) return null;
                  const r = Math.min(rx, h);
                  const d = `M ${x} ${H} V ${H-h+r} A ${r} ${r} 0 0 1 ${x+r} ${H-h} H ${x+w-r} A ${r} ${r} 0 0 1 ${x+w} ${H-h+r} V ${H} Z`;
                  return <path d={d} fill={`url(#${gradUrl})`} filter={`url(#${shadowUrl})`} />;
                };

                return (
                  <g key={i}>
                    {drawBar(bx - bW, bW - 0.2, bH1, "barPrimaryGrad", "primaryShadow")}
                    {drawBar(bx + 0.2, bW - 0.2, bH2, "barSecondaryGrad", "secondaryShadow")}
                  </g>
                );
              })
            ) : (
              <>
                <path d={paths1.fillPath} fill="url(#primaryGrad)"/>
                <path d={paths2.strokePath} fill="none" stroke={colors.secondary} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" filter="url(#secondaryShadow)" vectorEffect="non-scaling-stroke"/>
                <path d={paths1.strokePath} fill="none" stroke={colors.primary} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" filter="url(#primaryShadow)" vectorEffect="non-scaling-stroke"/>
              </>
            )}
          </svg>

          <div 
            className="absolute inset-0 z-40 cursor-default" 
            onPointerLeave={() => setHoverIdx(null)}
            onPointerMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const percentX = (x / rect.width) * 100;
              let closestIdx = 0;
              let minDiff = 100;
              for (let i = 0; i < dataPoints.length; i++) {
                const diff = Math.abs(percentX - getX(i));
                if (diff < minDiff) {
                  minDiff = diff;
                  closestIdx = i;
                }
              }
              setHoverIdx(closestIdx);
            }}
          />

          {/* Hover Details */}
          {/* Hover Details */}
          {hoverIdx !== null && (() => {
              const tipY_percent = (Math.min(getY(dataPoints[hoverIdx]), getY(prevDataPoints[hoverIdx])) / H) * 100;
              const isTooRight = hoverIdx > dataPoints.length / 2;
              
              return (
              <>
                {chartType !== 'DUAL BAR' && (
                    <>
                        {/* Solid vertical black line from the top dot down to the x-axis */}
                        <div 
                          className="absolute bottom-0 border-l border-slate-700 pointer-events-none transition-all duration-150 ease-out z-10"
                          style={{ 
                            left: `${getX(hoverIdx)}%`, 
                            top: `${Math.min(getY(dataPoints[hoverIdx]), getY(prevDataPoints[hoverIdx])) / H * 100}%` 
                          }}
                        />
                    </>
                )}
                
                {chartType !== 'DUAL BAR' && (
                    <>
                        <div className={`absolute w-2.5 h-2.5 ${colors.bg1} rounded-full pointer-events-none transition-all duration-150 ease-out shadow-[0_0_0_3px_white]`}
                             style={{ left: `${getX(hoverIdx)}%`, top: `${(getY(dataPoints[hoverIdx]) / H) * 100}%`, transform: 'translate(-50%, -50%)', zIndex: 60 }} />
                        <div className={`absolute w-2.5 h-2.5 ${colors.bg2} rounded-full pointer-events-none transition-all duration-150 ease-out shadow-[0_0_0_3px_white]`}
                             style={{ left: `${getX(hoverIdx)}%`, top: `${(getY(prevDataPoints[hoverIdx]) / H) * 100}%`, transform: 'translate(-50%, -50%)', zIndex: 60 }} />
                    </>
                )}
                
                <div 
                  className="absolute pointer-events-none transition-all duration-150 ease-out z-[9999]"
                  style={{ 
                    left: `${getX(hoverIdx)}%`, 
                    top: `${tipY_percent}%`,
                    transform: `translate(${isTooRight ? 'calc(-100% - 20px)' : '20px'}, -50%)`
                  }}
                >
                  <div 
                    className="bg-white rounded-lg shadow-lg border border-slate-200 p-3 min-w-[150px] relative"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    <div className="text-[11px] font-bold text-slate-800 mb-2 border-b border-slate-100 pb-2">
                      {hoverLabels[hoverIdx]}
                    </div>
                    <div className="flex flex-col gap-2 pt-1">
                      <div className="flex justify-between items-center gap-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-sm ${colors.bg1}`}></div>
                          <span className="text-[11px] font-medium text-slate-500">This Year</span>
                        </div>
                        <span className="text-[11px] font-bold text-slate-800 tabular-nums">₹{dataPoints[hoverIdx]}</span>
                      </div>
                      <div className="flex justify-between items-center gap-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-sm ${colors.bg2}`}></div>
                          <span className="text-[11px] font-medium text-slate-500">Last Year</span>
                        </div>
                        <span className="text-[11px] font-bold text-slate-800 tabular-nums">₹{prevDataPoints[hoverIdx]}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )})()}
        </div>
        <div className="absolute left-12 right-0 bottom-0 h-6 flex items-end">
          <div className="relative w-full h-full">
            {xLabels.map((lbl, i) => lbl ? (
              <span key={i} className="absolute text-[10px] font-semibold text-slate-400 transform -translate-x-1/2" style={{ left: `${getX(i)}%`, fontFamily:"'Inter',system-ui,sans-serif" }}>{lbl}</span>
            ) : null)}
          </div>
        </div>
      </div>
    </div>
  );
};

DynamicRevenueChart.propTypes = {
  total: PropTypes.number.isRequired,
  sales: PropTypes.array,
};


export const CustomerRetentionWidget = ({ data = [] }) => {
  const maxVal = Math.max(...data.map(d => d.max || d.value), 1);
  return (
    <div className="flex flex-col gap-3 w-full">
      {data.map((item, idx) => {
        const pct = Math.round((item.value / maxVal) * 100);
        return (
          <div key={idx} className="flex flex-col gap-1">
            <div className="flex justify-between items-center">
              <span className="text-[11px] font-bold text-slate-600">{item.label}</span>
              <span className="text-[11px] font-bold text-slate-800">{item.value}</span>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700 ease-out"
                style={{ width: `${pct}%`, backgroundColor: item.color || '#3b82f6' }}
              ></div>
            </div>
            {item.subtitle && <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">{item.subtitle}</span>}
          </div>
        );
      })}
    </div>
  );
};

